/**
 * Building the comparison table.
 *
 * Kept separate from rendering because this is where all the judgement lives: the
 * extracted fields are defined by the prompts, so two documents need not carry the
 * same keys, the same number of regions, or the same shape of value. Deciding what
 * counts as "the same attribute" across documents is the whole problem, and it is
 * easier to reason about as data than as JSX.
 *
 * Three rules:
 *
 * * **Union, not intersection.** A key that only one document has still gets a
 *   row, with an em dash elsewhere. A field the other document is missing is
 *   usually the most interesting kind of difference, and an intersection would
 *   silently drop it.
 * * **First-seen key order.** Prompts list fields in a meaningful order (header
 *   before dimensions, say), so keys keep the order the documents present them in
 *   rather than being alphabetised.
 * * **Regions align by index.** Region 1 is compared with region 1. This is an
 *   assumption, not a fact - the model returns regions in reading order, which two
 *   drawings need not share - so the UI says so out loud.
 */
import type { DocumentDetail, Fields, Position } from "./api";
import { isPresent, prettyKey, valueLines } from "./format";

/** One document's value for one attribute. */
export interface CompareCell {
  text: string;
  /** False when this document has no value for the row's attribute. */
  present: boolean;
}

/** One attribute across every compared document. */
export interface CompareRow {
  /** The section this row belongs to: the header, or one region. */
  group: string;
  /**
   * Where the row's values sit in each document: the first region of the drawing
   * this section is, per column, or undefined for a column that has no such
   * drawing. Absent entirely on the header section, which belongs to the sheet
   * rather than to a place on it.
   *
   * One per column rather than one number, because the same drawing is region 2
   * in one revision and region 3 in the other - which is the whole reason the
   * sections are matched by name.
   */
  regions?: (number | undefined)[];
  label: string;
  cells: CompareCell[];
  /** True when the documents do not all agree, including by omission. */
  differs: boolean;
}

export interface Comparison {
  rows: CompareRow[];
  /** Section order, for rendering group headings. */
  groups: string[];
  differingRows: number;
  /** How many drawings are missing from at least one of the documents. */
  unmatchedDrawings: number;
}

const HEADER_GROUP = "Document header";

/** Prefix marking a value that came from a region's drawing rather than its fields. */
const DRAWING_PREFIX = "Drawing - ";

/**
 * Compare two or more documents attribute by attribute.
 *
 * @param documents The documents to compare, in the order their columns appear.
 * @returns Every row, flagged with whether the documents agree.
 */
export function buildComparison(documents: DocumentDetail[]): Comparison {
  const rows: CompareRow[] = [];
  const groups: string[] = [];

  const headerRows = compareSection(
    HEADER_GROUP,
    documents.map((document) => document.data.header),
  );
  if (headerRows.length > 0) {
    groups.push(HEADER_GROUP);
    rows.push(...headerRows);
  }

  // Sections are the sheet's own drawings, matched by name across documents.
  const perDocument = documents.map((document) => drawingsOf(document));
  const names: string[] = [];
  for (const drawings of perDocument) {
    for (const name of drawings.keys()) if (!names.includes(name)) names.push(name);
  }

  let unmatched = 0;
  for (const name of names) {
    const found = perDocument.map((drawings) => drawings.get(name));
    if (found.some((drawing) => drawing === undefined)) unmatched += 1;
    const sectionRows = compareSection(
      titleOf(found),
      found.map((drawing) => drawing?.values),
    );
    if (sectionRows.length > 0) {
      groups.push(titleOf(found));
      rows.push(
        ...sectionRows.map((row) => ({
          ...row,
          regions: found.map((drawing) => drawing?.main),
        })),
      );
    }
  }

  return {
    rows,
    groups,
    differingRows: rows.filter((row) => row.differs).length,
    unmatchedDrawings: unmatched,
  };
}

/**
 * One drawing of one document, as the comparison needs it.
 *
 * `values` merges every part of it, because how finely a sheet was divided is
 * the extraction's business and not a difference between two drawings. `first`
 * is the region the drawing starts at, which is what frames it on the sheet.
 */
interface Drawing {
  /** The sheet's own name for it, as written - the heading the table shows. */
  name: string;
  values: Fields;
  /**
   * The region to frame when this drawing is shown on the sheet: its largest
   * part. A drawing's first part is often its title strip, and framing a reader
   * on the words "SIDE VIEW" when they asked to see the side view is not what
   * they meant - the biggest part is the geometry or the table itself.
   */
  main: number;
  /** How big that part is, kept only to decide the above. */
  mainArea: number;
}

/**
 * Collect a document's regions into its drawings, keyed by name.
 *
 * Matching two documents region by region was wrong as soon as regions became
 * the parts of a drawing rather than the drawings themselves. Two revisions of
 * one sheet divide differently - nine parts against ten, in a different order -
 * so region 7 was the parts list in one and a projection note in the other, and
 * the table dutifully reported every value of both as a difference. The sheet's
 * own name for a drawing is stable where its part count is not: TOP VIEW is TOP
 * VIEW in both revisions.
 *
 * A document with no grouping falls back to one "drawing" per region, numbered -
 * which is the old behaviour, and the right one when there is nothing better to
 * match on.
 */
function drawingsOf(document: DocumentDetail): Map<string, Drawing> {
  const drawings = new Map<string, Drawing>();
  (document.data.positions ?? []).forEach((position, index) => {
    const name = position.group_label?.trim() || `Region ${index + 1}`;
    const key = name.toLowerCase();
    const area = boxArea(position.bbox);
    const existing = drawings.get(key);
    if (existing) {
      Object.assign(existing.values, regionValues(position) ?? {});
      if (area > existing.mainArea) {
        existing.main = index;
        existing.mainArea = area;
      }
      return;
    }
    drawings.set(key, {
      name,
      values: regionValues(position) ?? {},
      main: index,
      mainArea: area,
    });
  });
  return drawings;
}

/** The heading for a section: the name as the first document that has it writes it. */
function titleOf(found: (Drawing | undefined)[]): string {
  return found.find((drawing) => drawing !== undefined)?.name ?? "Drawing";
}

/**
 * Flatten one region into a single set of label/value pairs.
 *
 * A region carries `fields` and `drawing` separately, and both are readings the
 * user compares the same way - so they share one section, with drawing values
 * marked so it stays clear where a number came from.
 */
function regionValues(position: Position | undefined): Fields | undefined {
  if (!position) return undefined;
  const merged: Fields = { ...(position.fields ?? {}) };
  for (const [key, value] of Object.entries(position.drawing ?? {})) {
    merged[`${DRAWING_PREFIX}${key}`] = value;
  }
  return merged;
}

/**
 * Compare whatever each column currently has framed, rather than the whole
 * documents.
 *
 * The side-by-side viewer lets each column choose its own region, precisely
 * because regions do not correspond across documents by index. This turns that
 * choice into a table: one row per attribute the chosen things carry between them,
 * one column per document, the same union/first-seen/differs rules as the full
 * comparison.
 *
 * @param documents The documents, in column order.
 * @param chosen Per document, the index of its framed region, or -1 for the whole
 *   sheet - which compares the document's header, since that is what "the sheet as
 *   a whole" records.
 * @returns One section's worth of rows.
 */
export function compareChosen(documents: DocumentDetail[], chosen: number[]): Comparison {
  const perDocument = documents.map((document, column) => {
    const index = chosen[column] ?? -1;
    if (index < 0) return document.data.header;
    return regionValues((document.data.positions ?? [])[index]);
  });

  const group = chosen.every((index) => (index ?? -1) < 0) ? HEADER_GROUP : "Framed region";
  const rows = compareSection(group, perDocument);

  return {
    rows,
    groups: rows.length > 0 ? [group] : [],
    differingRows: rows.filter((row) => row.differs).length,
    // Nothing to report: the user picked these two things deliberately, so no
    // matching was done on their behalf.
    unmatchedDrawings: 0,
  };
}

/** Build the rows for one section from each document's values for it. */
function compareSection(group: string, perDocument: (Fields | undefined)[]): CompareRow[] {
  const keys = unionOfKeys(perDocument);
  return keys.map((key) => {
    const cells = perDocument.map((values) => toCell(values?.[key]));
    return { group, label: label(key), cells, differs: disagree(cells) };
  });
}

/** Every key any document uses, in the order the documents first present them. */
function unionOfKeys(perDocument: (Fields | undefined)[]): string[] {
  const seen = new Set<string>();
  const order: string[] = [];
  for (const values of perDocument) {
    for (const key of Object.keys(values ?? {})) {
      if (seen.has(key)) continue;
      seen.add(key);
      order.push(key);
    }
  }
  return order;
}

function label(key: string): string {
  return key.startsWith(DRAWING_PREFIX)
    ? `${DRAWING_PREFIX}${prettyKey(key.slice(DRAWING_PREFIX.length))}`
    : prettyKey(key);
}

/**
 * One value as a table cell.
 *
 * A list is broken onto its own lines rather than joined with commas, matching
 * the inspector: a cell holding seven welding notes run together is unreadable,
 * and worse here than there, because two such cells side by side are supposed to
 * be compared line against line. The cell renders with `white-space: pre-line`,
 * and `disagree` normalises whitespace, so laying a value out over lines never
 * makes two equal values look different.
 */
function toCell(value: Fields[string] | undefined): CompareCell {
  if (value === undefined || !isPresent(value)) return { text: "", present: false };
  return { text: valueLines(value).join("\n"), present: true };
}

/**
 * Whether the documents disagree on one attribute.
 *
 * A missing value counts as a disagreement: "A says 3mm, B says nothing" is a
 * difference worth seeing, not a row to hide. Comparison ignores surrounding and
 * repeated whitespace, which the model varies without meaning anything by it, but
 * keeps case and punctuation - in a parts list those can be the difference.
 */
function disagree(cells: CompareCell[]): boolean {
  const [first, ...rest] = cells;
  if (first === undefined) return false;
  return rest.some(
    (cell) => cell.present !== first.present || normalize(cell.text) !== normalize(first.text),
  );
}

function normalize(text: string): string {
  return text.trim().replace(/\s+/g, " ");
}

/** A region's share of the page, for picking the part that best represents a drawing. */
function boxArea(bbox: Position["bbox"]): number {
  if (!bbox) return 0;
  return Math.max(0, bbox[2] - bbox[0]) * Math.max(0, bbox[3] - bbox[1]);
}
